/**
 * Pagina publică de prezentare (marketing).
 *
 * Reguli respectate:
 *  • prețurile NU sunt hardcodate — vin din `GET /meta/plans`, deci se schimbă din admin;
 *  • niciun element decorativ gratuit: fiecare secțiune explică un beneficiu concret;
 *  • funcționează fără sesiune și fără JavaScript greu (fără biblioteci de animație).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Icon, Skeleton, cx, type IconName } from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatCurrency } from '../lib/hooks.js';
import { useSession } from '../lib/session.js';

interface Plan {
  code: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  billing_interval: string;
  trial_days: number;
  features: string[];
  limits: Record<string, number>;
}

const FEATURE_LABELS: Record<string, string> = {
  qr_menu: 'Meniu QR',
  digital_display: 'Digital Display',
  analytics: 'Analytics',
  promotions: 'Promoții',
  multi_location: 'Multi-locație',
  multi_language: 'Multi-limbă',
  ordering: 'Comenzi online',
  reservations: 'Rezervări',
  api_access: 'Acces API',
  white_label: 'White label',
};

const LIMIT_LABELS: Array<[string, string]> = [
  ['menus', 'meniuri'],
  ['products', 'produse'],
  ['qrcodes', 'coduri QR'],
  ['locations', 'locații'],
  ['displays', 'ecrane'],
  ['users', 'utilizatori'],
];

export function LandingPage() {
  const session = useSession();
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [plansFailed, setPlansFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Plan[]>('/meta/plans')
      .then((data) => {
        if (!cancelled) setPlans(data);
      })
      .catch(() => {
        if (!cancelled) setPlansFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const authenticated = session.status === 'authenticated';

  return (
    <div className="min-h-screen bg-surface">
      {/* ── Navigație ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 border-b border-ink-100 bg-surface/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
            <span className="font-display text-[19px] tracking-tight text-ink-950">Menuvia</span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex">
            {[
              ['Funcționalități', '#functii'],
              ['Cum funcționează', '#cum-functioneaza'],
              ['Prețuri', '#preturi'],
              ['Întrebări', '#intrebari'],
            ].map(([label, href]) => (
              <a key={href} href={href} className="text-body-s text-ink-600 transition-ui hover:text-ink-950">
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                Autentificare
              </Button>
            </Link>
            <Link to={authenticated ? '/app' : '/register'}>
              <Button size="sm">{authenticated ? 'Dashboard' : 'Începe gratuit'}</Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden border-b border-ink-100 bg-surface-2">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-[0.5]"
            style={{
              background: 'radial-gradient(60% 100% at 50% 0%, var(--color-brand-100) 0%, transparent 70%)',
            }}
          />
          <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:py-24">
            <div className="flex flex-col justify-center gap-6">
              <Badge tone="brand" className="w-fit">
                Meniu digital · QR · Digital Display
              </Badge>

              <h1 className="font-display text-[40px] leading-[1.05] tracking-tight text-ink-950 sm:text-[56px]">
                Un meniu.
                <br />
                Toate ecranele.
                <br />
                <span className="text-brand-600">Zero duplicări.</span>
              </h1>

              <p className="max-w-xl text-body-l text-ink-600">
                Menuvia ține meniul restaurantului într-un singur loc și îl publică instant pe codurile QR de pe
                mese, pe site și pe ecranele din sală. Modifici un preț o singură dată — se schimbă peste tot,
                fără să re-tipărești nimic.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link to={authenticated ? '/app' : '/register'}>
                  <Button size="lg" icon={<Icon name="sparkles" size={18} />}>
                    {authenticated ? 'Deschide dashboard-ul' : 'Creează cont gratuit'}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="secondary" size="lg" icon={<Icon name="external" size={18} />}>
                    Vezi demo live
                  </Button>
                </Link>
              </div>

              <p className="text-body-s text-ink-400">
                Demo: <span className="font-mono text-ink-600">demo@menuvia.ro</span> /{' '}
                <span className="font-mono text-ink-600">DemoMenuvia2026!</span> · fără card, fără instalare
              </p>
            </div>

            {/* Ilustrația „un preț → toate canalele” */}
            <div className="flex items-center">
              <SyncPreview />
            </div>
          </div>

          <div className="relative mx-auto grid w-full max-w-6xl gap-4 px-4 pb-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
            {[
              ['0', 'regenerări de coduri QR', 'Codul conține adresa, nu meniul.'],
              ['~1 s', 'publicare în toate canalele', 'Aceeași sursă pentru toate ecranele.'],
              ['6', 'limbi pe meniul public', 'RO, EN, DE, FR, IT, ES.'],
              ['offline', 'ecranele nu rămân goale', 'Ultimul meniu sincronizat continuă să ruleze.'],
            ].map(([value, label, hint]) => (
              <div key={label} className="rounded-[var(--radius-lg)] border border-ink-100 bg-surface p-4">
                <div className="font-display text-[26px] leading-none text-ink-950">{value}</div>
                <div className="mt-2 text-body-s font-medium text-ink-800">{label}</div>
                <div className="text-caption text-ink-400">{hint}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Cum funcționează ───────────────────────────────────────────── */}
        <section id="cum-functioneaza" className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
          <SectionHeading
            eyebrow="Cum funcționează"
            title="Trei pași până la primul scan"
            subtitle="Fără instalări, fără agent local, fără training. Meniul se construiește în browser, iar ecranele se conectează cu un cod."
          />

          <ol className="grid gap-5 md:grid-cols-3">
            {[
              {
                step: '01',
                title: 'Construiești meniul o singură dată',
                text: 'Categorii, produse, prețuri, alergeni, poze, variante și promoții. Totul într-un singur catalog, valabil pentru toate locațiile.',
              },
              {
                step: '02',
                title: 'Generezi și tipărești codurile QR',
                text: 'Coduri separate pentru masă, bar sau terasă. Codul conține doar adresa meniului, deci nu se schimbă niciodată când actualizezi prețurile.',
              },
              {
                step: '03',
                title: 'Conectezi ecranele din sală',
                text: 'TV-ul primește un cod de împerechere, iar conținutul se sincronizează automat. Dacă internetul cade, ecranul continuă cu ultimul meniu salvat.',
              },
            ].map((item) => (
              <li key={item.step}>
                <Card className="flex h-full flex-col gap-3">
                  <span className="font-mono text-caption tracking-[0.18em] text-brand-600">{item.step}</span>
                  <h3 className="font-display text-[21px] leading-snug text-ink-950">{item.title}</h3>
                  <p className="text-body-s text-ink-600">{item.text}</p>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Canale ─────────────────────────────────────────────────────── */}
        <section className="border-y border-ink-100 bg-surface-2">
          <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
            <SectionHeading
              eyebrow="Un meniu, patru canale"
              title="Aceeași sursă de adevăr, oriunde ajunge clientul"
              subtitle="Nu există „meniul de pe QR” și „meniul de pe TV”. Există un catalog, iar canalele îl citesc în timp real."
            />

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: 'qr' as IconName,
                  title: 'Meniu QR',
                  text: 'Pagină mobilă ultra-rapidă, fără aplicație. Filtre pe alergeni, căutare, 6 limbi.',
                },
                {
                  icon: 'globe' as IconName,
                  title: 'Meniu web & SEO',
                  text: 'Subdomeniu propriu, date structurate Restaurant/Menu, sitemap, indexare corectă.',
                },
                {
                  icon: 'display' as IconName,
                  title: 'Digital Display',
                  text: 'Șabloane pe TV, programare pe ore și zile, comenzi de la distanță, offline-first.',
                },
                {
                  icon: 'grid' as IconName,
                  title: 'API & integrări',
                  text: 'API versionat /api/v1 pentru POS, agregatoare sau aplicații proprii.',
                },
              ].map((channel) => (
                <Card key={channel.title} className="flex flex-col gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)] bg-brand-100 text-brand-700">
                    <Icon name={channel.icon} size={19} />
                  </span>
                  <h3 className="text-body-l font-medium text-ink-950">{channel.title}</h3>
                  <p className="text-body-s text-ink-600">{channel.text}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── Funcționalități ────────────────────────────────────────────── */}
        <section id="functii" className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
          <SectionHeading
            eyebrow="Ce include platforma"
            title="Construit pentru operațiunea reală a unui restaurant"
            subtitle="Nu un panou de administrare generic: fiecare ecran răspunde la o întrebare pe care o are managerul în timpul serviciului."
          />

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: 'menu' as IconName,
                title: 'Menu builder profesionist',
                points: [
                  'Categorii, produse, galerii, alergeni, calorii, gramaj, SKU',
                  'Preț nou vs. preț vechi, disponibilitate pe locație',
                  'Variante și extra-uri (structura este pregătită)',
                ],
              },
              {
                icon: 'qr' as IconName,
                title: 'QR manager',
                points: [
                  'Coduri separate pe masă, bar, terasă',
                  'Descărcare PNG/SVG și variante de print',
                  'Statistici: scanări, dispozitiv, limbă',
                ],
              },
              {
                icon: 'display' as IconName,
                title: 'Displays & player',
                points: [
                  'Împerechere cu cod (ABC-7291), heartbeat, online/offline',
                  'Șabloane peisaj/portret, programare, promoții active',
                  'Mod offline cu resincronizare automată',
                ],
              },
              {
                icon: 'chart' as IconName,
                title: 'Analytics privacy-friendly',
                points: [
                  'Vizualizări meniu, scanări QR, produse în top',
                  'Ore de vârf, locații, activitate ecrane',
                  'Fără date personale colectate inutil',
                ],
              },
              {
                icon: 'shield' as IconName,
                title: 'Roluri & izolare totală',
                points: [
                  'Owner, Manager, Editor, Staff — permisiuni granulare',
                  'Fiecare restaurant vede strict datele lui',
                  'Audit log pentru fiecare modificare',
                ],
              },
              {
                icon: 'pin' as IconName,
                title: 'Multi-locație & abonamente',
                points: [
                  'Locații independente, meniu comun sau adaptat',
                  'Planuri configurabile din admin, fără cod',
                  'Pregătit pentru Stripe',
                ],
              },
            ].map((feature) => (
              <Card key={feature.title} className="flex flex-col gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)] bg-surface-3 text-ink-800">
                  <Icon name={feature.icon} size={19} />
                </span>
                <h3 className="text-body-l font-medium text-ink-950">{feature.title}</h3>
                <ul className="flex flex-col gap-1.5">
                  {feature.points.map((point) => (
                    <li key={point} className="flex gap-2 text-body-s text-ink-600">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                      {point}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Prețuri (din API, nu hardcodate) ───────────────────────────── */}
        <section id="preturi" className="border-y border-ink-100 bg-surface-2">
          <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
            <SectionHeading
              eyebrow="Prețuri"
              title="Planuri configurabile, fără costuri ascunse"
              subtitle="Prețurile și limitele sunt administrate din platformă, nu din cod. Poți începe gratuit și trece la un plan plătit când crește restaurantul."
            />

            {plansFailed ? (
              <Card className="text-body-s text-ink-600">
                Tarifele nu au putut fi încărcate acum. Le poți vedea după autentificare, în secțiunea Abonament.
              </Card>
            ) : !plans ? (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-72" />
                ))}
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {plans.map((plan) => (
                  <Card
                    key={plan.code}
                    className={cx(
                      'flex flex-col gap-4',
                      plan.code === 'pro' && 'border-brand-600 shadow-[var(--shadow-elevated)]',
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-[22px] text-ink-950">{plan.name}</h3>
                      {plan.code === 'pro' ? <Badge tone="brand">recomandat</Badge> : null}
                    </div>

                    <div className="flex items-end gap-1">
                      <span className="font-display text-[32px] leading-none text-ink-950 nums">
                        {plan.price_cents === 0 ? 'Gratuit' : formatCurrency(plan.price_cents, plan.currency)}
                      </span>
                      {plan.price_cents > 0 ? <span className="text-body-s text-ink-400">/lună</span> : null}
                    </div>

                    <p className="text-body-s text-ink-600">{plan.description}</p>

                    <ul className="flex flex-1 flex-col gap-1.5">
                      {plan.features.slice(0, 5).map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-body-s text-ink-800">
                          <Icon name="check" size={15} className="text-brand-600" />
                          {FEATURE_LABELS[feature] ?? feature}
                        </li>
                      ))}
                      <li className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-caption text-ink-400">
                        {LIMIT_LABELS.filter(([key]) => plan.limits[key] !== undefined).map(([key, label]) => (
                          <span key={key} className="nums">
                            {plan.limits[key]} {label}
                          </span>
                        ))}
                      </li>
                    </ul>

                    <Link to={plan.code === 'free' ? '/register' : '/register'}>
                      <Button variant={plan.code === 'pro' ? 'primary' : 'secondary'} className="w-full">
                        {plan.price_cents === 0 ? 'Începe gratuit' : `Alege ${plan.name}`}
                      </Button>
                    </Link>

                    {plan.trial_days > 0 ? (
                      <p className="text-caption text-ink-400">{plan.trial_days} zile de probă, fără card.</p>
                    ) : null}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── Întrebări frecvente ────────────────────────────────────────── */}
        <section id="intrebari" className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 lg:py-20">
          <SectionHeading eyebrow="Întrebări frecvente" title="Ce întreabă de obicei un restaurant" />

          <div className="flex flex-col gap-3">
            {[
              {
                q: 'Trebuie să re-tipăresc codurile QR când schimb prețurile?',
                a: 'Nu. Codul QR conține doar adresa meniului tău, nu meniul în sine. Modifici prețurile în dashboard, iar clienții care scanează văd automat versiunea nouă.',
              },
              {
                q: 'Ce se întâmplă dacă pică internetul în restaurant?',
                a: 'Ecranele din sală salvează ultimul meniu sincronizat și continuă să îl afișeze. Când conexiunea revine, se resincronizează singure, fără intervenție.',
              },
              {
                q: 'Cine poate modifica meniul?',
                a: 'Decizi tu. Owner și Manager au acces complet, Editorul construiește meniul, Staff-ul vede doar ce are nevoie. Fiecare acțiune importantă ajunge în jurnalul de audit.',
              },
              {
                q: 'Am mai multe locații. Pot folosi același meniu?',
                a: 'Da. Construiești catalogul o dată și îl publici pe fiecare locație, cu prețuri sau disponibilitate adaptate. Codurile QR rămân valabile pentru fiecare masă.',
              },
            ].map((item) => (
              <details key={item.q} className="group rounded-[var(--radius-lg)] border border-ink-100 bg-surface p-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-body-l font-medium text-ink-950">
                  {item.q}
                  <Icon
                    name="chevron-down"
                    size={18}
                    className="shrink-0 text-ink-400 transition-ui group-open:rotate-180"
                  />
                </summary>
                <p className="mt-3 text-body-s text-ink-600">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── Demo live ─────────────────────────────────────────────────── */}
        <section className="border-t border-ink-100">
          <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
            <div className="flex flex-col gap-5">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-100 px-3 py-1 text-caption font-medium text-brand-800">
                <Icon name="sparkles" size={14} />
                Demo deschis, fără cont
              </span>
              <h2 className="font-display text-[30px] leading-tight text-ink-950">
                Scanează și vezi exact ce vede clientul tău
              </h2>
              <p className="max-w-xl text-body text-ink-600">
                Restaurantul demo <strong className="font-medium text-ink-900">Green Garden</strong> are meniul
                complet publicat: 8 secțiuni, 27 de produse, variante, alergeni, promoția „Happy Hour” și trei
                limbi (română, engleză, germană). Codul din dreapta este unul real — îl poți scana cu telefonul.
              </p>
              <ul className="flex flex-col gap-2 text-body-s text-ink-600">
                {[
                  'Pagină mobilă, încărcare instant, fără aplicație instalată',
                  'Comutator de limbă și filtre pe preferințe alimentare',
                  'Codul conține adresa meniului, nu meniul — deci nu se regenerează niciodată',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2">
                    <Icon name="check" size={16} className="mt-0.5 shrink-0 text-brand-600" />
                    {line}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <a href="/r/green-garden" target="_blank" rel="noreferrer">
                  <Button size="lg" icon={<Icon name="external" size={17} />}>
                    Deschide meniul demo
                  </Button>
                </a>
                <a href="/q/demo-green-garden" target="_blank" rel="noreferrer">
                  <Button variant="secondary" size="lg">
                    Testează traseul din QR
                  </Button>
                </a>
              </div>
            </div>

            <Card className="mx-auto flex w-full max-w-sm flex-col items-center gap-4 text-center">
              <img
                src="/api/v1/public/qr/demo-green-garden.png?size=512"
                alt="Codul QR al mesei 1 din restaurantul demo Green Garden"
                width={236}
                height={236}
                className="mt-2 h-[236px] w-[236px]"
              />
              <p className="text-body-s text-ink-500">
                Codul QR al mesei 1 din Green Garden, cu logo-ul restaurantului în centru și nivel de corecție H.
              </p>
            </Card>
          </div>
        </section>

        {/* ── CTA final ─────────────────────────────────────────────────── */}
        <section className="border-t border-ink-100 bg-ink-950">
          <div className="mx-auto flex w-full max-w-6xl flex-col items-start gap-6 px-4 py-14 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-[30px] leading-tight text-white">
                Publică meniul corect în toate canalele, în mai puțin de o oră
              </h2>
              <p className="max-w-2xl text-body-s text-ink-200">
                Creezi contul, adaugi restaurantul și locația, construiești meniul, generezi codurile QR și
                conectezi primul ecran. Fără instalări, fără contract pe termen lung.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={authenticated ? '/app' : '/register'}>
                <Button size="lg">{authenticated ? 'Deschide dashboard-ul' : 'Începe gratuit'}</Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary" size="lg">
                  Intră în cont
                </Button>
              </Link>
              <a href="/r/green-garden" target="_blank" rel="noreferrer">
                <Button variant="ghost" size="lg" icon={<Icon name="external" size={17} />}>
                  Vezi un meniu real
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="bg-ink-950 pb-10 text-ink-200">
        <div className="mx-auto grid w-full max-w-6xl gap-8 border-t border-white/10 px-4 pt-10 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Logo tone="light" />
              <span className="font-display text-[19px] text-white">Menuvia</span>
            </div>
            <p className="max-w-xs text-caption text-ink-400">
              Platformă SaaS pentru meniuri digitale, coduri QR și ecrane în restaurant. Meniul central rămâne
              singura sursă de adevăr.
            </p>
          </div>

          <FooterColumn
            title="Produs"
            links={[
              ['Funcționalități', '#functii'],
              ['Prețuri', '#preturi'],
              ['Cum funcționează', '#cum-functioneaza'],
              ['Întrebări frecvente', '#intrebari'],
            ]}
          />
          <FooterColumn
            title="Cont"
            links={[
              ['Autentificare', '/login'],
              ['Creează cont', '/register'],
              ['Am uitat parola', '/forgot-password'],
            ]}
          />
          <FooterColumn
            title="Platformă"
            links={[
              ['Stare API', '/api/v1/health'],
              ['Configurare publică', '/api/v1/meta/public-config'],
              ['Admin platformă', '/admin'],
            ]}
          />
        </div>

        <div className="mx-auto mt-8 flex w-full max-w-6xl flex-col gap-2 px-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <span className="text-caption text-ink-400">© {new Date().getFullYear()} Menuvia. Toate drepturile rezervate.</span>
          <span className="text-caption text-ink-400">
            Analytics fără cookie-uri de urmărire · Conform GDPR
          </span>
        </div>
      </footer>
    </div>
  );
}

/* ────────────────────────────── sub-componente ───────────────────────────── */

function SectionHeading({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) {
  return (
    <div className="mb-8 flex max-w-2xl flex-col gap-2">
      <span className="font-mono text-caption uppercase tracking-[0.18em] text-brand-600">{eyebrow}</span>
      <h2 className="font-display text-[30px] leading-tight tracking-tight text-ink-950 sm:text-[34px]">{title}</h2>
      {subtitle ? <p className="text-body-s text-ink-600">{subtitle}</p> : null}
    </div>
  );
}

function FooterColumn({ title, links }: { title: string; links: Array<[string, string]> }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-body-s font-medium text-white">{title}</span>
      {links.map(([label, href]) =>
        href.startsWith('#') ? (
          <a key={href} href={href} className="text-body-s text-ink-400 transition-ui hover:text-white">
            {label}
          </a>
        ) : (
          <Link key={href} to={href} className="text-body-s text-ink-400 transition-ui hover:text-white">
            {label}
          </Link>
        ),
      )}
    </div>
  );
}

function Logo({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  return (
    <span
      className={cx(
        'flex h-8 w-8 items-center justify-center rounded-[10px]',
        tone === 'dark' ? 'bg-brand-600 text-white' : 'bg-brand-500 text-white',
      )}
      aria-hidden
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
      </svg>
    </span>
  );
}

/**
 * Demonstrația vizuală a principiului central: o modificare de preț se propagă în toate canalele.
 * Folosește doar CSS (fără biblioteci), respectă `prefers-reduced-motion` din stilurile globale.
 */
function SyncPreview(): ReactNode {
  const [price, setPrice] = useState(39);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => setPrice(35), 4000),
      window.setTimeout(() => setPrice(39), 9000),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [price]);

  const channels: Array<{ icon: IconName; label: string; detail: string }> = [
    { icon: 'qr', label: 'Meniu QR', detail: 'masa 7 · terasă' },
    { icon: 'globe', label: 'Meniu web', detail: 'SEO · 6 limbi' },
    { icon: 'display', label: 'Display sală', detail: 'TV bar · portret' },
    { icon: 'grid', label: 'API / POS', detail: 'integrare' },
  ];

  return (
    <Card className="w-full gap-5 border-ink-100 bg-surface p-6 shadow-[var(--shadow-elevated)]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-mono text-caption uppercase tracking-[0.16em] text-ink-400">Catalog central</span>
          <span className="font-display text-[20px] text-ink-950">Pizza Diavola</span>
          <span className="text-caption text-ink-400">Categoria: Pizza · 1 produs modificat</span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-body-s text-ink-400 line-through nums">35,00 lei</span>
          <span className="font-display text-[26px] leading-none text-brand-700 nums">
            {price === 39 ? '39,00 lei' : '35,00 lei'}
          </span>
          <Badge tone="brand">preț nou</Badge>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {channels.map((channel, index) => (
          <div
            key={channel.label}
            className="flex items-center gap-3 rounded-[var(--radius-md)] border border-ink-100 bg-surface-2 px-3 py-2.5"
            style={{ animation: `page-enter 260ms ease-out ${index * 90}ms both` }}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] bg-surface text-ink-800">
              <Icon name={channel.icon} size={16} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-body-s font-medium text-ink-950">{channel.label}</span>
              <span className="text-caption text-ink-400">{channel.detail}</span>
            </div>
            <span className="flex items-center gap-1.5 text-caption text-success">
              <Icon name="check" size={14} />
              sincronizat
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-ink-100 pt-4">
        <span className="text-caption text-ink-400">Publicat acum câteva secunde</span>
        <span className="flex items-center gap-2 text-caption text-ink-600">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          fără regenerare de QR
        </span>
      </div>
    </Card>
  );
}
